import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API_V1_PREFIX, isProd } from '../../utils/constants';
import { fireCheckoutDetailsSubmitted, fireDiscountApplied, fireDiscountRemoved } from '../../utils/dataLayer';
import './account.scss';
import TextInput from '../../components/BaseComponents/TextInput';
import PhoneInputComp from '../../components/BaseComponents/PhoneInput';
import { toast } from 'react-toastify';
import { Helmet } from 'react-helmet';
import Footer from '../../components/common/Footer';
import img1 from '../../assets/images/img1.png';
import img2 from '../../assets/images/img2.png';
import img3 from '../../assets/images/img3.png';
import img4 from '../../assets/images/img4.png';
import img5 from '../../assets/images/img5.png';
import arrow from '../../assets/icons/Chevron-down.svg';
import sliderArrow from '../../assets/icons/sliderArrow.svg';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import bannerImg1 from '../../assets/images/BannerCoreVitalsMembership.png';
import bannerImg2 from '../../assets/images/BannerCoreVitalsPlusMembership.png';
import bannerImg3 from '../../assets/images/BannerFullBodyMRI.png';
import checkIcon from '../../assets/icons/CheckTick.svg';
import AccordArrow from '../../assets/icon-components/AccordArrow';
import useMediaQuery from '@mui/material/useMediaQuery';
import arrowRightIcon from '../../assets/icons/whiteArrow.png';
import arrowLeftIcon from '../../assets/icons/arrow-left.svg';
import ErrorDisplay from '../../components/BaseComponents/ErrorDisplay';
import { categorizeError, ErrorInfo } from '../../utils/errorHandler';
import { clearCheckoutAddonsStorage } from '../../utils/checkoutAddons';
import { renderSelectedPlanPrice } from '../../utils/rhPromoPricing';
import { useRhReferralPricing } from '../../utils/useRhReferralPricing';

interface SignupFormData {
  fullName: string;
  email: string;
  phone: string;
  promocode: string;
  acceptTerms?: boolean;
}

interface SignupResponse {
  email: string;
  first_name: string;
  last_name: string;
  payment_url: string;
  access_token: string;
  phone: string;
  client_secret: string;
  subscription_id: string;
  customer_id: string;
  payment_intent: any;
  amount: number;
}

interface SelectedPlanData {
  type: 'coreVitals' | 'coreVitalsPlus' | 'fullBody';
  testCount: number | null;
  product: {
    key: string;
    name: string;
    monthly_amount: number | null;
    yearly_amount: number | null;
    stripe_price_monthly_id: string;
    stripe_price_yearly_id: string;
    active: boolean;
  } | null;
  productId: string | null;
  paymentType?: 'monthly' | 'yearly';
}

const submitLeadToRhForm = (data: { name: string; email: string; phone: string }) => {
  const rhForm = (window as any).RH_MF70bd716e94;
  if (rhForm?.pendingReferral) {
    rhForm.pendingReferral(data);
  }
};




const Account: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const leadSubmittedRef = useRef(false);
  const [formData, setFormData] = useState<SignupFormData>({
    fullName: '',
    email: '',
    phone: '',
    promocode: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Partial<SignupFormData>>({});
  const [errorInfo, setErrorInfo] = useState<ErrorInfo | null>(null);
  const [showCoupon, setShowCoupon] = useState(false);
  const [couponApplied, setCouponApplied] = useState(false);
  const [keepMeUpdated, setKeepMeUpdated] = useState(true);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [termsError, setTermsError] = useState<string>('');
  const [isMobile, setIsMobile] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [maxSlideIndex, setMaxSlideIndex] = useState(0);
  const [selectedPlan, setSelectedPlan] = useState<SelectedPlanData | null>(null);
  const [showWhatIncludes, setShowWhatIncludes] = useState<boolean>(false);
  const [showOrderSummary, setShowOrderSummary] = useState<boolean>(false);
  const [membershipType, setMembershipType] = useState<string | null>(null);
  const isMobileView = useMediaQuery('(max-width: 767px)');
  useRhReferralPricing();
  const sliderRef = useRef<Slider>(null);
  const isSubmittingRef = useRef(false);
  const totalSlides = 5;
  const itemWidth = 218;
  const gap = 24;
  const [searchParams] = useSearchParams();
  const tokenParam = searchParams.get('token');

  useEffect(() => {
    const checkScreenSize = () => {
      const container = document.querySelector('.c_container');
      if (container) {
        const containerWidth = container.clientWidth;
        setIsMobile(containerWidth < 1248);
        
        const itemsPerView = Math.floor((containerWidth + gap) / (itemWidth + gap));
        const maxSlide = Math.max(0, totalSlides - itemsPerView);
        setMaxSlideIndex(maxSlide);
      } else {
        setIsMobile(window.innerWidth < 1248);
        const itemsPerView = Math.floor((window.innerWidth + gap) / (itemWidth + gap));
        const maxSlide = Math.max(0, totalSlides - itemsPerView);
        setMaxSlideIndex(maxSlide);
      }
    };

    checkScreenSize();
    
    const timeoutId = setTimeout(checkScreenSize, 100);
    
    window.addEventListener('resize', checkScreenSize);
    
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', checkScreenSize);
    };
  }, []);

  useEffect(() => {
    if (isMobile) {
      setCurrentSlide(0);
    }
  }, [isMobile]);

  useEffect(() => {
    setIsLoading(false);
    setErrors({});

    const storedPlan = localStorage.getItem('selectedPlan');
    if (storedPlan) {
      try {
        const planData = JSON.parse(storedPlan);
        setSelectedPlan(planData);
      } catch (error) {
        // Error parsing selected plan
      }
    }

    const navigationEntry = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
    const isReload = navigationEntry?.type === 'reload';

    if (isReload) {
      sessionStorage.removeItem('signupFormData');
      sessionStorage.removeItem('keepMeUpdated');
      sessionStorage.removeItem('acceptTerms');
      localStorage.removeItem('appliedPromocode');
    } else {
      const savedFormData = sessionStorage.getItem('signupFormData');
      if (savedFormData) {
        try {
          const formDataObj = JSON.parse(savedFormData);
          setFormData(formDataObj);
        } catch (error) {
          // Error parsing form data
        }
      }

      const savedKeepMeUpdated = sessionStorage.getItem('keepMeUpdated');
      if (savedKeepMeUpdated !== null) {
        setKeepMeUpdated(savedKeepMeUpdated === 'true');
      }

      const savedAcceptTerms = sessionStorage.getItem('acceptTerms');
      if (savedAcceptTerms !== null) {
        setAcceptTerms(savedAcceptTerms === 'true');
      }
    }

    const savedPromocode = localStorage.getItem('appliedPromocode');
    if (savedPromocode) {
      setCouponApplied(true);
    }

    window.scrollTo(0, 0);
    const scrollTimeout = setTimeout(() => {
      window.scrollTo(0, 0);
    }, 100);

    return () => {
      clearTimeout(scrollTimeout);
    };
  }, []);

  // If a token is present (existing member), fetch their details and prefill the form
  useEffect(() => {
    if (!tokenParam) return;

    const fetchMemberDetails = async () => {
      try {
        const baseURL = isProd
          ? process.env.REACT_APP_API_BASE_URL_PROD
          : process.env.REACT_APP_API_BASE_URL_DEV;
        const response = await axios.get(
          `${baseURL}/${API_V1_PREFIX}members?loading_strategy=with_subscription`,
          {
            headers: {
              Authorization: `Bearer ${tokenParam}`,
            },
          }
        );

        const member = response.data;

        // Store membership_type for navigation
        if (member.membership_type) {
          setMembershipType(member.membership_type);
        }

        setFormData((prev) => ({
          ...prev,
          fullName: [member.first_name, member.last_name].filter(Boolean).join(' ') || prev.fullName,
          email: member.email || prev.email,
          phone:
            (member.phone || prev.phone || '').replace(/^\+/, '') ||
            prev.phone,
        }));
      } catch (error) {
        console.error('Error fetching member details for signup:', error);
      }
    };

    fetchMemberDetails();
  }, [tokenParam]);

  useEffect(() => {
    setIsLoading(false);
  }, [location.pathname]);

  useEffect(() => {
    const handlePageShow = () => {
      isSubmittingRef.current = false;
      setIsLoading(false);
    };

    const handleVisibilityChange = () => {
      if (!document.hidden && !isSubmittingRef.current) {
        setIsLoading(false);
      }
    };

    const handleFocus = () => {
      if (!isSubmittingRef.current) {
        setIsLoading(false);
      }
    };

    window.addEventListener('pageshow', handlePageShow);
    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      window.removeEventListener('pageshow', handlePageShow);
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const isValidName = (name: string): boolean => {
    const nameRegex = /^[a-zA-Z\s\-'.]+$/;
    return nameRegex.test(name);
  };

  const isNumericOnly = (name: string): boolean => {
    return /^\d+$/.test(name.trim());
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<SignupFormData> = {};

    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Full name is required';
    } else {
      const nameParts = formData.fullName.trim().split(' ').filter(part => part.length > 0);
      
      if (nameParts.length < 2) {
        newErrors.fullName = 'Please enter both first and last name';
      } else {
        const invalidParts: string[] = [];
        
        nameParts.forEach((part, index) => {
          const partName = index === 0 ? 'First name' : 'Last name';
          
          if (isNumericOnly(part)) {
            invalidParts.push(`${partName} cannot contain numbers`);
          } else if (!isValidName(part)) {
            invalidParts.push(`${partName} contains invalid characters`);
          }
        });
        
        if (invalidParts.length > 0) {
          if (invalidParts.length === 1) {
            newErrors.fullName = invalidParts[0];
          } else {
            newErrors.fullName = 'Name contains invalid characters. Please use only letters';
          }
        }
      }
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else {
      const phoneRegex = /^[+]?[\d\s\-()]+$/;
      const cleanPhone = formData.phone.replace(/\D/g, '');
      if (!phoneRegex.test(formData.phone) || cleanPhone.length < 10) {
        newErrors.phone = 'Please enter a valid phone number with country code';
      }
    }

    if (!acceptTerms) {
      setTermsError('You must accept the Terms & Conditions and Privacy Policy to continue');
    } else {
      setTermsError('');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0 && acceptTerms;
  };

  const splitFullName = (fullName: string): { first_name: string; last_name: string } => {
    const nameParts = fullName.trim().split(' ');
    if (nameParts.length === 1) {
      return { first_name: nameParts[0], last_name: '' };
    }
    const first_name = nameParts[0];
    const last_name = nameParts.slice(1).join(' ');
    return { first_name, last_name };
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (name === 'fullName' && value.trim()) {
      const nameParts = value.trim().split(' ').filter(part => part.length > 0);
      
      if (nameParts.length >= 2) {
        const invalidParts: string[] = [];
        
        nameParts.forEach((part, index) => {
          const partName = index === 0 ? 'First name' : 'Last name';
          
          if (isNumericOnly(part)) {
            invalidParts.push(`${partName} cannot contain numbers`);
          } else if (!isValidName(part)) {
            invalidParts.push(`${partName} contains invalid characters`);
          }
        });
        
        if (invalidParts.length > 0) {
          const errorMsg = invalidParts.length === 1 
            ? invalidParts[0] 
            : 'Name contains invalid characters. Please use only letters';
          setErrors(prev => ({
            ...prev,
            [name]: errorMsg
          }));
        } else {
          setErrors(prev => ({
            ...prev,
            [name]: undefined
          }));
        }
      } else {
        setErrors(prev => ({
          ...prev,
          [name]: undefined
        }));
      }
    } else {
      if (errors[name as keyof SignupFormData]) {
        setErrors(prev => ({
          ...prev,
          [name]: undefined
        }));
      }
    }
  };

  const handlePhoneChange = (phone: string) => {
    setFormData(prev => ({
      ...prev,
      phone: phone
    }));
    if (errors.phone) {
      setErrors(prev => ({
        ...prev,
        phone: undefined
      }));
    }
  };

  const handleApplyCoupon = () => {
    if (formData.promocode.trim()) {
      setCouponApplied(true);
      fireDiscountApplied(formData.promocode.trim());
      toast.success('Coupon code saved! If valid, discount will be applied on the next step.');
    } else {
      toast.error('Please enter a coupon code');
    }
  };

  const handleRemoveCoupon = () => {
    const removedCode = formData.promocode.trim();
    setCouponApplied(false);
    setFormData(prev => ({
      ...prev,
      promocode: ''
    }));
    fireDiscountRemoved(removedCode);
    toast.success('Coupon code removed');
  };

  const handleBackToPlans = async () => {
    if (!tokenParam) {
      navigate('/plans');
      return;
    }

    // If token is present, check membership_type and redirect accordingly
    let membershipTypeToUse = membershipType;

    // If membership_type is not yet loaded, fetch it
    if (!membershipTypeToUse) {
      try {
        const baseURL = isProd
          ? process.env.REACT_APP_API_BASE_URL_PROD
          : process.env.REACT_APP_API_BASE_URL_DEV;
        const response = await axios.get(
          `${baseURL}/${API_V1_PREFIX}members?loading_strategy=with_subscription`,
          {
            headers: {
              Authorization: `Bearer ${tokenParam}`,
            },
          }
        );
        membershipTypeToUse = response.data.membership_type;
        setMembershipType(membershipTypeToUse);
      } catch (error) {
        console.error('Error fetching membership type:', error);
        navigate('/plans');
        return;
      }
    }

    // Redirect based on membership_type
    const membershipTypeLower = membershipTypeToUse?.toLowerCase();
    const tokenQuery = `?token=${encodeURIComponent(tokenParam)}`;

    if (membershipTypeLower === 'core') {
      navigate(`/plans-core${tokenQuery}`);
    } else if (membershipTypeLower === 'plus') {
      navigate(`/plans-plus${tokenQuery}`);
    } else if (membershipTypeLower === 'scan') {
      navigate(`/plans-scan${tokenQuery}`);
    } else {
      // Fallback to default plans page if membership_type is unknown
      navigate('/plans');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    sessionStorage.removeItem('signupFormData');
    sessionStorage.removeItem('keepMeUpdated');

    isSubmittingRef.current = true;
    setIsLoading(true);

    const productId = selectedPlan?.productId || null;

    if (tokenParam) {
      try {
        const baseURL = isProd
          ? process.env.REACT_APP_API_BASE_URL_PROD
          : process.env.REACT_APP_API_BASE_URL_DEV;

        const { first_name, last_name } = splitFullName(formData.fullName);
        const formattedPhone =
          formData.phone[0] !== '+' ? '+' + formData.phone : formData.phone;

        // Submit lead to RH before core API calls (once per page load)
        if (!leadSubmittedRef.current) {
          leadSubmittedRef.current = true;
          submitLeadToRhForm({
            name: formData.fullName.trim(),
            email: formData.email.trim(),
            phone: formattedPhone,
          });
        }

        const updatePayload: any = {
          email: formData.email,
          first_name,
          last_name,
          phone: formattedPhone,
        };

        if (formData.promocode && formData.promocode.trim()) {
          updatePayload.promocode = formData.promocode.trim();
        }

        if (productId) {
          updatePayload.product_id = productId;
        }

        await axios.post(
          `${baseURL}/${API_V1_PREFIX}members/plans/update-details`,
          updatePayload,
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${tokenParam}`,
            },
          }
        );

        sessionStorage.setItem('signupFormData', JSON.stringify(formData));
        sessionStorage.setItem('keepMeUpdated', keepMeUpdated.toString());
        sessionStorage.setItem('acceptTerms', acceptTerms.toString());

        if (formData.promocode && formData.promocode.trim()) {
          localStorage.setItem('appliedPromocode', formData.promocode.trim());
        } else {
          localStorage.removeItem('appliedPromocode');
        }

        isSubmittingRef.current = false;
        setIsLoading(false);

        // Fire checkout details submitted event
        fireCheckoutDetailsSubmitted();
        
        // After details, take the user to add-ons selection (then payment).
        window.location.href = `/add-ons-plan?token=${tokenParam}`;
        return;
      } catch (error: any) {
        isSubmittingRef.current = false;
        setIsLoading(false);
        console.error('Error handling existing member flow:', error);

        if (axios.isAxiosError(error) && error.response && error.response.data) {
          const data: any = error.response.data;
          const apiMessage =
            data.detail ||
            data.message ||
            data.error ||
            (typeof data === 'string' ? data : null);

          if (apiMessage) {
            toast.error(apiMessage);
            return;
          }
        }

        toast.error('Something went wrong. Please try again.');
        return;
      }
    }

    try {
      const baseURL = isProd
        ? process.env.REACT_APP_API_BASE_URL_PROD
        : process.env.REACT_APP_API_BASE_URL_DEV;

      const { first_name, last_name } = splitFullName(formData.fullName);
      const formattedPhone =
        formData.phone[0] !== '+' ? '+' + formData.phone : formData.phone;
      const productId = selectedPlan?.productId || null;

      // Submit lead to RH before core signup call (once per page load)
      if (!leadSubmittedRef.current) {
        leadSubmittedRef.current = true;
        submitLeadToRhForm({
          name: formData.fullName.trim(),
          email: formData.email.trim(),
          phone: formattedPhone,
        });
      }

      const signupData = {
        email: formData.email,
        first_name,
        last_name,
        phone: formattedPhone,
        promocode: formData.promocode || undefined,
        ...(productId && { product_id: productId }),
      };

      const response = await axios.post<SignupResponse>(
        `${baseURL}/${API_V1_PREFIX}members/signup`,
        signupData,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      const signupResponseData = {
        ...response.data,
        promocode: formData.promocode || undefined,
      };
      localStorage.setItem('signupResponse', JSON.stringify(signupResponseData));
      localStorage.setItem(
        'subscriptionAmount',
        signupResponseData?.amount?.toString()
      );

      sessionStorage.setItem('signupFormData', JSON.stringify(formData));
      sessionStorage.setItem('keepMeUpdated', keepMeUpdated.toString());
      sessionStorage.setItem('acceptTerms', acceptTerms.toString());
      
      if (formData.promocode && formData.promocode.trim()) {
        localStorage.setItem('appliedPromocode', formData.promocode.trim());
      } else {
        localStorage.removeItem('appliedPromocode');
      }
      
      const accessToken = response.data.access_token;
      if (!accessToken) {
        toast.error('Signup successful but missing access token. Please try again.');
        isSubmittingRef.current = false;
        setIsLoading(false);
        return;
      }
      
      isSubmittingRef.current = false;

      // Fire checkout details submitted event
      fireCheckoutDetailsSubmitted();
      
      // After signup, take the user to add-ons selection (then payment).
      clearCheckoutAddonsStorage();
      window.location.href = `/add-ons-plan?token=${accessToken}`;
      
    } catch (error) {
      isSubmittingRef.current = false;
      setIsLoading(false);
      
      const categorizedError = categorizeError(error);
      if (
        categorizedError.type === 'access_restricted' ||
        categorizedError.type === 'network_error' ||
        categorizedError.type === 'session_expired'
      ) {
        setErrorInfo(categorizedError);
        return;
      }
      setErrorInfo(null);
      
      if (axios.isAxiosError(error) && error.response && error.response.data) {
        const errorData = error.response.data as { detail?: string | any; message?: string; description?: string };
        
        const newErrors: Partial<SignupFormData> = {};
        let hasFieldErrors = false;
        let errorSource = errorData.description || errorData.detail;
        
        if (errorSource) {
          try {
            let parsedErrors: any = null;
            
            if (typeof errorSource === 'string') {
              try {
                parsedErrors = JSON.parse(errorSource);
              } catch {
                try {
                  let cleanedString = errorSource.replace(
                    /loc['"]?\s*:\s*\(([^)]+)\)/g,
                    (match, tupleContent) => {
                      const items = tupleContent.split(',').map((item: string) => {
                        const trimmed = item.trim();
                        return trimmed.replace(/^['"]|['"]$/g, '');
                      });
                      return `loc: [${items.map((item: string) => `"${item}"`).join(', ')}]`;
                    }
                  );
                  cleanedString = cleanedString.replace(/'/g, '"');
                  parsedErrors = JSON.parse(cleanedString);
                } catch {
                  const errorMatches = errorSource.matchAll(/loc['"]?\s*:\s*\([^)]*['"]([^'"]+)['"][^)]*\)/g);
                  const msgMatches = errorSource.matchAll(/msg['"]?\s*:\s*['"]([^'"]+)['"]/g);
                  
                  const errors: any[] = [];
                  const locArray = Array.from(errorMatches);
                  const msgArray = Array.from(msgMatches);
                  
                  for (let i = 0; i < Math.max(locArray.length, msgArray.length); i++) {
                    if (locArray[i] && msgArray[i]) {
                      errors.push({
                        loc: ['body', locArray[i][1]],
                        msg: msgArray[i][1]
                      });
                    }
                  }
                  
                  if (errors.length > 0) {
                    parsedErrors = errors;
                  }
                }
              }
            } else {
              parsedErrors = errorSource;
            }
            
            if (Array.isArray(parsedErrors)) {
              parsedErrors.forEach((err: any) => {
                let fieldName = '';
                let errorMsg = err.msg || err.message || 'Invalid value';
                
                const formatErrorMessage = (msg: string, field: string): string => {
                  let formatted = msg.trim();
                  const fieldLabel = field === 'first_name' ? 'First name' : field === 'last_name' ? 'Last name' : 'Name';
                  
                  if (formatted.toLowerCase().includes('contains invalid characters')) {
                    const hasNumericValue = /\d+/.test(formatted) && formatted.includes(':');
                    
                    if (hasNumericValue) {
                      formatted = `${fieldLabel} contains invalid characters. Please use only letters`;
                    } else {
                      formatted = `${fieldLabel} contains invalid characters`;
                    }
                  } else {
                    formatted = formatted.replace(/^Value error,\s*/i, '');
                    formatted = formatted.replace(/:\s*['"]?[^'"]*['"]?\s*$/i, '');
                    formatted = formatted.replace(/first_name/gi, 'First name');
                    formatted = formatted.replace(/last_name/gi, 'Last name');
                    
                    if (formatted.length > 0) {
                      formatted = formatted.charAt(0).toUpperCase() + formatted.slice(1);
                    }
                  }
                  
                  return formatted || 'Invalid value';
                };
                
                if (err.loc) {
                  if (Array.isArray(err.loc)) {
                    const bodyIndex = err.loc.findIndex((item: any) => item === 'body' || item === 'Body');
                    if (bodyIndex >= 0 && bodyIndex < err.loc.length - 1) {
                      fieldName = err.loc[bodyIndex + 1];
                    } else {
                      fieldName = err.loc[err.loc.length - 1];
                    }
                  } else if (typeof err.loc === 'string') {
                    fieldName = err.loc;
                  }
                }
                
                if (fieldName === 'first_name' || fieldName === 'last_name') {
                  const formattedMsg = formatErrorMessage(errorMsg, fieldName);
                  newErrors.fullName = formattedMsg;
                  hasFieldErrors = true;
                } else if (fieldName === 'email') {
                  newErrors.email = formatErrorMessage(errorMsg, fieldName);
                  hasFieldErrors = true;
                } else if (fieldName === 'phone' || fieldName === 'phone_number') {
                  newErrors.phone = formatErrorMessage(errorMsg, fieldName);
                  hasFieldErrors = true;
                }
              });
            }
          } catch (parseError) {
          }
        }
        
        if (hasFieldErrors) {
          setErrors(newErrors);
        } else {
          const errorMessage = typeof errorSource === 'string' 
            ? errorSource 
            : errorData.message || errorData.description || 'Signup failed. Please try again.';
          toast.error(errorMessage);
        }
      } else {
        toast.error('An unexpected error occurred. Please try again.');
      }
    }
  };

  const getPlanBannerImg = () => {
    if (!selectedPlan) return null;
    if (selectedPlan.type === 'fullBody') return bannerImg3;
    if (selectedPlan.type === 'coreVitalsPlus') return bannerImg2;
    return bannerImg1;
  };

  const getPlanFeatures = () => {
    if (!selectedPlan) return [];
    if (selectedPlan.type === 'fullBody') {
      return [
        'In-app full-body scan report',
        'Doctor review + consult',
        'Pre and post scan clinician consults',
        'Report organised by body part',
        '60-90 minutes in the scanner'
      ];
    }
    
    const testCount = selectedPlan.testCount || 1;
    const testText = `${testCount} blood biomarker test${testCount > 1 ? 's' : ''} per year`;
    
    if (selectedPlan.type === 'coreVitalsPlus') {
      return [
        testText,
        'Includes free nurse blood draw at home',
        'Full biomarker panel',
        'Doctor review + consult',
        'Nutritionist guidance',
        'App access to track progress',
        'Annual full-body MRI',
        'Pre- and post-scan clinician consults',
        'Combined blood and MRI insights in-app',
        'Ongoing tracking and progress monitoring'
      ];
    }
    return [
      testText,
      'Includes free nurse blood draw at home',
      'Full biomarker panel',
      'Doctor review + consult',
      'Nutritionist guidance',
      'App access to track progress'
    ];
  };

  const getPlanPriceDisplay = () => {
    if (!selectedPlan) return '£40/month';
    return renderSelectedPlanPrice(selectedPlan);
  };

  const hasSelectedPlan = selectedPlan !== null;

  return (
    <>
    <Helmet>
      <meta name="robots" content="noindex, nofollow, noarchive, nosnippet, noimageindex" />
      <meta name="googlebot" content="noindex, nofollow, noarchive, nosnippet, noimageindex" />
      <meta name="bingbot" content="noindex, nofollow, noarchive, nosnippet, noimageindex" />
      <title>Sign Up - CoreVitals</title>
    </Helmet>
    <div className={`c_container ${hasSelectedPlan ? 'signup-from-plans' : ''}`}>
      {errorInfo ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 'calc(100vh - 100px)', width: '100%', padding: '40px 20px' }}>
          <ErrorDisplay errorInfo={errorInfo} />
        </div>
      ) : hasSelectedPlan ? (
        <div className='two-colum-grid signup-form-layout'>
          <div className='left-column'>
            <div className='enter-details-header'>{tokenParam ? 'Update your personal details' : 'Capture your personal details'}</div>
            <h2 className='select-plan-title'>{tokenParam ? 'Update your details' : 'Enter your details'}</h2>
            <form onSubmit={handleSubmit} className='your-details-form'>
              <div className='input-label'>
                <label className='labelStyle'>
                  Full name<span className='required'>*</span>
                </label>
                <TextInput 
                  type='text' 
                  name='fullName' 
                  placeholder='Enter your full name' 
                  value={formData.fullName}
                  onChange={handleInputChange}
                />
                {errors.fullName && <div className='error-message'>{errors.fullName}</div>}
              </div>
              
              <div className='input-label top-space'>
                <label className='labelStyle'>
                  Email address<span className='required'>*</span>
                </label>
                <TextInput 
                  type='email' 
                  name='email' 
                  placeholder='Enter your email address' 
                  value={formData.email}
                  onChange={handleInputChange}
                />
                {errors.email && <div className='error-message'>{errors.email}</div>}
              </div>
              
              <div className='input-label top-space'>
                <label className='labelStyle'>
                  Phone number<span className='required'>*</span>
                </label>
                <PhoneInputComp
                  placeholder='Enter your phone number'
                  selectedPhone={formData.phone}
                  onPhoneChange={handlePhoneChange}
                />
                {errors.phone && <div className='error-message'>{errors.phone}</div>}
              </div>
            </form>
            
            <div className='input-label keep-me-updated'>
              <label className='d-flex align-items-center gap-20'>
                <input type='checkbox' checked={keepMeUpdated} onChange={() => setKeepMeUpdated(!keepMeUpdated)} />
                <span className='checkbox-style' ></span>
                <span>Keep me updated on member-only events and offers.</span>
              </label>
            </div>
            
            {!keepMeUpdated && <div className='privacy-policy-text'>
            We collect your responses to understand your interests, tailor relevant content to you, and let you know when a spot becomes available to join CoreVitals. Your data is stored securely and handled in accordance with our privacy policy. <br/>
            
            We will never share your personal information with third parties without your consent. For more information, please review our privacy policy at: <a href="https://www.corevitals.me/privacy-policy" target="_blank" rel="noopener noreferrer">https://www.corevitals.me/privacy-policy</a>
            </div>}

            <div className='input-label keep-me-updated' style={{ paddingTop: '8px' }}>
              <label className='d-flex align-items-center gap-20'>
                <input 
                  type='checkbox' 
                  checked={acceptTerms} 
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setAcceptTerms(checked);
                    sessionStorage.setItem('acceptTerms', checked.toString());
                    if (checked) {
                      setTermsError('');
                    }
                  }} 
                />
                <span className='checkbox-style' ></span>
                <span>
                  I agree to the{' '}
                  <a 
                    href="https://www.corevitals.me/member-terms-and-conditions" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    style={{ color: '#336C42', textDecoration: 'underline' }}
                  >
                    Terms & Conditions
                  </a>
                  {' '}and{' '}
                  <a 
                    href="https://www.corevitals.me/privacy-policy" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    style={{ color: '#336C42', textDecoration: 'underline' }}
                  >
                    Privacy Policy
                  </a>
                </span>
              </label>
            </div>
            {termsError && <div className='error-message' style={{ marginTop: '8px' }}>{termsError}</div>}
            <button onClick={() => setShowCoupon(!showCoupon)} type='button' className='discount-code-btn'>
              Have a discount code?
              <img src={arrow} alt="Arrow" />
            </button>
            {showCoupon && <div className='discount-code-section'>
              <div className='discount-code-container'>
                <label className='discount-code-label'>Discount code</label>
                <div className='discount-input-group'>
                  <TextInput 
                    type='text' 
                    name='promocode' 
                    placeholder='Enter discount code here' 
                    value={formData.promocode}
                    onChange={handleInputChange}
                    isDisabled={couponApplied}
                  />
                  <button
                    type='button'
                    className='apply-discount-btn'
                    onClick={couponApplied ? handleRemoveCoupon : handleApplyCoupon}
                  >
                    <span>{couponApplied ? 'REMOVE' : 'APPLY'}</span>&nbsp;
                    <span>DISCOUNT</span>
                  </button>
                </div>
              </div>
            </div>}
            <div style={{ display: 'flex', gap: '16px', marginTop: '56px' }}>
              <button
                type='button'
                onClick={handleBackToPlans}
                className='plans-step-btn'
                style={{ marginTop: 0, flex: 1 }}
              >
                <img src={arrowLeftIcon} alt='back' />
                Back to plans
              </button>
              <button
                type='button'
                onClick={handleSubmit}
                className='plans-step-btn'
                disabled={isLoading || !acceptTerms}
                style={{ marginTop: 0, flex: 1 }}
              >
                {isLoading ? (tokenParam ? 'Updating' : 'Signing up...') : 'Continue'} <img src={arrowRightIcon} alt='arrow right' />
              </button>
            </div>
          </div>
          <div className='right-column'>
            <div className={`inner-card plan-info ${showOrderSummary ? 'active' : ''}`}>
              <h4 
                onClick={() => isMobileView && setShowOrderSummary(!showOrderSummary)} 
                className={`inner-card-title ${isMobileView ? 'd-flex justify-content-between align-items-center' : ''}`}
              >
                Order summary {isMobileView && <AccordArrow />}
              </h4>
              {selectedPlan && (
                <div>
                 
                  <div className='order-summary-content'>
                    <img 
                      className='order-summary-banner-img' 
                      src={getPlanBannerImg() || ''} 
                      alt={`${selectedPlan.product?.name || 'Plan'} banner`} 
                    />
                    <h3 className='order-summary-title'>
                      {selectedPlan.product?.name || (selectedPlan.type === 'fullBody' ? 'Full-body MRI' : 'CoreVitals Membership')}
                    </h3>
                    <p className='order-summary-description'>
                      {selectedPlan.type === 'fullBody' 
                        ? 'A one-time internal snapshot'
                        : selectedPlan.type === 'coreVitalsPlus'
                        ? 'CoreVitals+ Membership – Deeper insight across bloods combined with MRI imaging.'
                        : 'Ongoing health tracking without imaging - up to 4x blood tests/year'}
                    </p>
                    {isMobileView ? (
                      <ul className='order-summary-list'>
                        {getPlanFeatures().map((item, index) => (
                          <li key={index}>
                            <img src={checkIcon} alt='check' />
                            {item}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <div 
                        onClick={() => setShowWhatIncludes(!showWhatIncludes)} 
                        className={`what-includes ${showWhatIncludes ? 'active' : ''} ${selectedPlan.type}`}
                      >
                        <div className='what-includes-title'>
                          <span>What's included</span>
                          <AccordArrow />
                        </div>
                        <div className='what-includes-content'>
                          <ul className='order-summary-list'>
                            {getPlanFeatures().map((item, index) => (
                              <li key={index}>
                                <img src={checkIcon} alt='check' />
                                {item}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className={`total-price ${isMobileView ? '' : 'no-border'}`}>
                    <div>Total</div>
                    <div>{getPlanPriceDisplay()}</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div>
          <div className='form-row' >
            <div className='form-col' >
              <h3 className='form-title'>{tokenParam ? 'Update your details' : 'Enter your details'}</h3>
              <form onSubmit={handleSubmit}>
                <div className='input-label'>
                  <label className='labelStyle'>
                    Full name<span className='required'>*</span>
                  </label>
                  <TextInput 
                    type='text' 
                    name='fullName' 
                    placeholder='Enter name' 
                    value={formData.fullName}
                    onChange={handleInputChange}
                  />
                  {errors.fullName && <div className='error-message'>{errors.fullName}</div>}
                </div>
                
                <div className='input-label top-space'>
                  <label className='labelStyle'>
                    Email address<span className='required'>*</span>
                  </label>
                  <TextInput 
                    type='email' 
                    name='email' 
                    placeholder='Enter email' 
                    value={formData.email}
                    onChange={handleInputChange}
                  />
                  {errors.email && <div className='error-message'>{errors.email}</div>}
                </div>
                
                <div className='input-label top-space'>
                  <label className='labelStyle'>
                    Phone number<span className='required'>*</span>
                  </label>
                  <PhoneInputComp
                    placeholder='Enter phone number'
                    selectedPhone={formData.phone}
                    onPhoneChange={handlePhoneChange}
                  />
                  {errors.phone && <div className='error-message'>{errors.phone}</div>}
                </div>
              </form>
              
              <div className='input-label keep-me-updated'>
                <label className='d-flex align-items-center gap-20'>
                  <input type='checkbox' checked={keepMeUpdated} onChange={() => setKeepMeUpdated(!keepMeUpdated)} />
                  <span className='checkbox-style' ></span>
                  <span>Keep me updated on member-only events and offers.</span>
                </label>
              </div>
              
              {!keepMeUpdated && <div className='privacy-policy-text'>
              We collect your responses to understand your interests, tailor relevant content to you, and let you know when a spot becomes available to join CoreVitals. Your data is stored securely and handled in accordance with our privacy policy. <br/>
              
              We will never share your personal information with third parties without your consent. For more information, please review our privacy policy at: <a href="https://www.corevitals.me/privacy-policy">https://www.corevitals.me/privacy-policy</a>
              </div>}

              <div className='input-label keep-me-updated' style={{ paddingTop: '8px' }}>
                <label className='d-flex align-items-center gap-20'>
                  <input 
                    type='checkbox' 
                    checked={acceptTerms} 
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setAcceptTerms(checked);
                      sessionStorage.setItem('acceptTerms', checked.toString());
                      if (checked) {
                        setTermsError('');
                      }
                    }} 
                  />
                  <span className='checkbox-style' ></span>
                  <span>
                    I agree to the{' '}
                    <a 
                      href="https://www.corevitals.me/member-terms-and-conditions" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      style={{ color: '#336C42', textDecoration: 'underline' }}
                    >
                      Terms & Conditions
                    </a>
                    {' '}and{' '}
                    <a 
                      href="https://www.corevitals.me/privacy-policy" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      style={{ color: '#336C42', textDecoration: 'underline' }}
                    >
                      Privacy Policy
                    </a>
                  </span>
                </label>
              </div>
              {termsError && <div className='error-message' style={{ marginTop: '8px' }}>{termsError}</div>}
              <button onClick={() => setShowCoupon(!showCoupon)} className='discount-code-btn'>
                Have a discount code?
                <img src={arrow} alt="Arrow" />
              </button>
              {showCoupon && <div className='discount-code-section'>
                <div className='discount-code-container'>
                  <label className='discount-code-label'>Discount code</label>
                  <div className='discount-input-group'>
                    <TextInput 
                      type='text' 
                      name='promocode' 
                      placeholder='Enter discount code here' 
                      value={formData.promocode}
                      onChange={handleInputChange}
                      isDisabled={couponApplied}
                    />
                    <button
                      type='button'
                      className='apply-discount-btn'
                      onClick={couponApplied ? handleRemoveCoupon : handleApplyCoupon}
                    >
                      <span>{couponApplied ? 'REMOVE' : 'APPLY'}</span>&nbsp;
                      <span>DISCOUNT</span>
                    </button>
                  </div>
                </div>
              </div>}
              <button
                  type='button'
                  className='continue-btn'
                  onClick={handleSubmit}
                  disabled={isLoading || !acceptTerms}
                >
                  {isLoading ? (tokenParam ? 'Updating' : 'Signing up...') : 'CONTINUE'}
              </button>
            </div>
            <div className='form-sidebar' >
              <div className='membership-card' >
                <div className='membership-card-inner'>
                  <h4>CoreVitals Membership</h4>
                  <div className='membership-per-month' >
                    £40<span>/month</span>
                  </div>
                  <div className='membership-billed-annually' >Billed annually</div>
                  <div className='membership-per-cancel' >Covers 12 months of testing and care. <span>Cancel anytime.</span></div>
                </div>
              </div>
              <div className='sidebar-list' >
                <h4>You're investing in the health you'll have for years to come.</h4>
                <ul>
                  <li>Twice-yearly blood tests (100+ biomarkers)</li>
                  <li>1:1 clinician review</li>
                  <li>1:1 nutritionist review</li>
                  <li>Add previous blood results</li>
                  <li>Unlimited support</li>
                  <li>Personalised health plans</li>
                </ul>
              </div>
            </div>
          </div>
      <div className='what-happens-after-join' >
        <h3 className='setion-title'>What happens after you join?</h3>
        {isMobile ? (
          <Slider
            ref={sliderRef}
            className='steps-slider'
            dots={false}
            infinite={false}
            speed={500}
            slidesToScroll={1}
            arrows={true}
            variableWidth={true}
            afterChange={(index: number) => setCurrentSlide(index)}
            prevArrow={
              <button
                type="button"
                className={`custom-arrow custom-prev-arrow ${currentSlide === 0 ? 'slick-disabled' : ''}`}
                aria-label="Previous"
                disabled={currentSlide === 0}
              >
                <img src={sliderArrow} alt="Previous" />
              </button>
            }
            nextArrow={
              <button
                type="button"
                className={`custom-arrow custom-next-arrow ${currentSlide >= maxSlideIndex ? 'slick-disabled' : ''}`}
                aria-label="Next"
                disabled={currentSlide >= maxSlideIndex}
              >
                <img src={sliderArrow} alt="Next" />
              </button>
            }
          >
            <div className='step-item-container first-step' >
              <div className='step-item-number' >
                <span>1</span>
              </div>
              <div className='step-item-content' >
                <img src={img1} alt="" />
                <h6>Book your test</h6>
                <h4>Choose your nearest clinic.</h4>
              </div>
            </div>
            <div className='step-item-container' >
              <div className='step-item-number' >
                <span>2</span>
              </div>
              <div className='step-item-content' >
                <img src={img2} alt="" />
                <h6>Receive kit</h6>
                <h4>We ship you your first test kit. </h4>
              </div>
            </div>
            <div className='step-item-container' >
              <div className='step-item-number' >
                <span>3</span>
              </div>
              <div className='step-item-content' >
                <img src={img3} alt="" />
                <h6>Attend test</h6>
                <h4>Give your sample (10 minutes).</h4>
              </div>
            </div>
            <div className='step-item-container' >
              <div className='step-item-number' >
                <span>4</span>
              </div>
              <div className='step-item-content' >
                <img src={img4} alt="" />
                <h6>See your results</h6>
                <h4>In 5 working days, your results are ready.</h4>
              </div>
            </div>
            <div className='step-item-container' >
              <div className='step-item-number' >
                <span>5</span>
              </div>
              <div className='step-item-content' >
                <img src={img5} alt="" />
                <h6>Meet your clinician</h6>
                <h4>Your doctor walks you through your bloodwork and next steps.</h4>
              </div>
            </div>
          </Slider>
        ) : (
          <div className='steps-container d-flex' >
            <div className='step-item-container first-step' >
              <div className='step-item-number' >
                <span>1</span>
              </div>
              <div className='step-item-content' >
                <img src={img1} alt="" />
                <h6>Book your test</h6>
                <h4>Choose your nearest clinic.</h4>
              </div>
            </div>
            <div className='step-item-container' >
              <div className='step-item-number' >
                <span>2</span>
              </div>
              <div className='step-item-content' >
                <img src={img2} alt="" />
                <h6>Receive kit</h6>
                <h4>We ship you your first test kit. </h4>
              </div>
            </div>
            <div className='step-item-container' >
              <div className='step-item-number' >
                <span>3</span>
              </div>
              <div className='step-item-content' >
                <img src={img3} alt="" />
                <h6>Attend test</h6>
                <h4>Give your sample (10 minutes).</h4>
              </div>
            </div>
            <div className='step-item-container' >
              <div className='step-item-number' >
                <span>4</span>
              </div>
              <div className='step-item-content' >
                <img src={img4} alt="" />
                <h6>See your results</h6>
                <h4>In 5 working days, your results are ready.</h4>
              </div>
            </div>
            <div className='step-item-container' >
              <div className='step-item-number' >
                <span>5</span>
              </div>
              <div className='step-item-content' >
                <img src={img5} alt="" />
                <h6>Meet your clinician</h6>
                <h4>Your doctor walks you through your bloodwork and next steps.</h4>
              </div>
            </div>
          </div>
        )}
      </div>
        </div>
        )}
    </div>
    <Footer />
    </>
  );
};

export default Account;
